import { useMemo, useState } from 'react'

import { css, Global } from '@emotion/react'
import { useForm } from 'react-hook-form'
import { useLoaderData } from 'react-router-dom'

import { breakpoints, Text } from '@cash-design-system/react'
import { AuthorizeDigitalWalletPaymentBlocker } from '@cash-web/protos/squareup/franklin/blockers/authorize_digital_wallet_blocker.pb'
import { useQueryGetGooglePayJwtToken } from '@cash-web/shared-blocker-data-access-api'
import { useTextTokenizer } from '@cash-web/shared-feature-use-text-tokenizer'
import useMediaQuery from '@cash-web/shared-ui-use-media-query'

import { BaseBlocker } from '../../BaseBlocker/BaseBlocker'
import { BlockerLoading } from '../../BlockerLoading/BlockerLoading'
import { ApplePayButton, userCanUseApplePay } from './ApplePayButton/ApplePayButton'
import { GooglePayButton, useGooglePayReadiness } from './GooglePayButton/GooglePayButton'
import { isValidMoney } from './validity'

export const BlockerAuthorizeDigitalWalletPayment = ({
  blocker,
}: {
  blocker: AuthorizeDigitalWalletPaymentBlocker
}) => {
  const [submitting, setSubmitting] = useState(false)
  const loaderData = useLoaderData() as { jwtToken: string } | undefined
  const initialJwtToken = loaderData?.jwtToken

  const textTokenizer = useTextTokenizer()
  const isMobile = !useMediaQuery(breakpoints.tablet)
  const form = useForm()

  const {
    authorization_amount: authorizationAmount,
    card_brand_types: cardBrandTypes,
    supported_cash_instrument_types: supportedCashInstrumentTypes,
    allowable_issuer_countries: allowableIssuerCountries,
    require_billing_address: requireBillingAddress,
    require_payer_phone: requirePayerPhone,
    require_payer_email: requirePayerEmail,
    require_shipping_address: requireShippingAddress,
    require_payer_name: requirePayerName,
    processing_fee: processingFee,
    title,
    subtitle,
    disclaimer_text: disclaimerText,
  } = blocker

  if (!isValidMoney(authorizationAmount)) {
    throw Error(
      `BlockerAuthorizeDigitalWalletPayment: authorization_amount is invalid [amount=${authorizationAmount?.amount}] [currency_code=${authorizationAmount?.currency_code}]`
    )
  }

  if (!cardBrandTypes || cardBrandTypes.length === 0) {
    throw Error(
      `BlockerAuthorizeDigitalWalletPayment: card_brand_types is invalid [card_brand_types=${cardBrandTypes}]`
    )
  }

  if (!supportedCashInstrumentTypes || supportedCashInstrumentTypes.length === 0) {
    throw Error(
      `BlockerAuthorizeDigitalWalletPayment: supported_cash_instrument_types is invalid [supported_cash_instrument_types=${supportedCashInstrumentTypes}]`
    )
  }

  if (!allowableIssuerCountries) {
    throw Error(
      `BlockerAuthorizeDigitalWalletPayment: allowable_issuer_countries is invalid [allowable_issuer_countries=${allowableIssuerCountries}]`
    )
  }

  const canUseApplePay = userCanUseApplePay()

  const {
    isGooglePayReady,
    isLoading: isCheckingGooglePayReadiness,
    googlePaymentsClient,
    cardPaymentMethod,
  } = useGooglePayReadiness({
    cashInstrumentTypes: cardBrandTypes,
    supportedInstrumentTypes: supportedCashInstrumentTypes,
    billingAddressRequired: requireBillingAddress,
    phoneNumberRequired: requirePayerPhone,
    allowableIssuerCountries: [allowableIssuerCountries],
  })

  const { data: jwtTokenData, isLoading: isLoadingJwtToken } = useQueryGetGooglePayJwtToken({
    queryOptions: {
      enabled: !initialJwtToken,
      initialData: initialJwtToken ? { token: initialJwtToken } : undefined,
    },
  })

  const buttons = useMemo(() => {
    const buttonList = []

    if (isGooglePayReady && jwtTokenData?.token) {
      buttonList.push(
        <GooglePayButton
          key="google-pay"
          amount={authorizationAmount}
          emailRequired={requirePayerEmail}
          shippingAddressRequired={requireShippingAddress}
          setSubmitting={setSubmitting}
          googlePaymentsClient={googlePaymentsClient}
          cardPaymentMethod={cardPaymentMethod}
          jwtToken={jwtTokenData.token}
        />
      )
    }

    if (canUseApplePay) {
      buttonList.push(
        <ApplePayButton
          key="apple-pay"
          amount={authorizationAmount}
          processingFee={processingFee}
          cashInstrumentTypes={cardBrandTypes}
          requirePayerName={requirePayerName}
          requirePhoneNumber={requirePayerPhone}
          requirePayerEmail={requirePayerEmail}
          requireBillingAddress={requireBillingAddress}
          requireShippingAddress={requireShippingAddress}
          supportedInstrumentTypes={supportedCashInstrumentTypes}
          allowableIssuerCountries={[allowableIssuerCountries]}
          setSubmitting={setSubmitting}
        />
      )
    }

    return buttonList
  }, [
    authorizationAmount,
    cardBrandTypes,
    supportedCashInstrumentTypes,
    allowableIssuerCountries,
    requirePayerEmail,
    requireShippingAddress,
    processingFee,
    requirePayerName,
    requirePayerPhone,
    requireBillingAddress,
    canUseApplePay,
    isGooglePayReady,
    jwtTokenData,
    googlePaymentsClient,
    cardPaymentMethod,
  ])

  const isCheckingReadiness = isCheckingGooglePayReadiness || isLoadingJwtToken

  if (isCheckingReadiness) {
    return null
  }

  if (submitting) {
    return <BlockerLoading />
  }

  return (
    <>
      <Global
        styles={css`
          apple-pay-button {
            --apple-pay-button-border-radius: 6.25rem;
            --apple-pay-button-height: 3.25rem;
            --apple-pay-button-padding: 0;
            --apple-pay-button-box-sizing: border-box;
            min-height: unset;
          }
        `}
      />
      <BaseBlocker
        mainText={title}
        secondaryText={
          subtitle && (
            <Text typography="body" color="textSubtle">
              {textTokenizer(subtitle)}
            </Text>
          )
        }
        form={form}
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        onSubmit={() => {}}
        // Reverse the buttons on mobile to match the design
        buttons={isMobile ? buttons.slice().reverse() : buttons}
        shouldStackButtons={isMobile}
        disclaimerText={disclaimerText}
      />
    </>
  )
}
